import { Box, Checkbox, styled, TableCell, TableRow, Typography } from '@mui/material';
import React from 'react';
import { ClinicalReviewModel } from '../../../lib/models/ClinicalReviewModel';
import Link from '../../Utils/Link';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { useScreenLargerThan } from '../../../lib/hooks/breakpoint-hooks';
import { ColKeys } from './ClinicalReviewListing';

export type ClinicalReviewItemProps = {
    item: ClinicalReviewModel;
    removeColumns?: ColKeys[];
};

const ClinicalReviewItem = ({ item, removeColumns }: ClinicalReviewItemProps) => {
    const isLargerThanMedium = useScreenLargerThan('md');
    const [inProgressState, setInProgressState] = React.useState(false);

    const TableDataCell = styled(TableCell)({
        borderBottom: 'none',
        padding: isLargerThanMedium ? '1rem' : '0.188rem',
        wordBreak: 'break-word'
    });

    const BorderTableDataCell = styled(TableCell)({
        padding: isLargerThanMedium ? '1rem' : '0.188rem',
        wordBreak: 'break-word'
    });

    const SemiBoldTableDataCell = styled(TableDataCell)({
        fontWeight: '600',
        // padding: '16px',
        wordBreak: 'break-word'
    });

    const RedBox = styled(Box)({
        color: 'red!important',
        fontSize: isLargerThanMedium ? 15 : 13
    });

    const NormalBox = styled(Box)({
        color: 'secondary.main',
        fontSize: isLargerThanMedium ? 15 : 13
    });

    const BoldTypography = styled(Typography)({
        fontWeight: 'bold',
        fontSize: isLargerThanMedium ? 13 : 10,
        color: 'inherit'
    });
    const NoBoldTypography = styled(Typography)({
        fontWeight: '600',
        fontSize: isLargerThanMedium ? 13 : 10,
        color: 'inherit'
    });

    const SemiBoldTypography = styled(Typography)({
        fontWeight: '600',
        fontSize: isLargerThanMedium ? 14 : 11
    });

    const StyledEmailIcon = styled(MarkEmailUnreadOutlinedIcon)(({ theme }) => ({
        verticalAlign: 'middle',
        marginLeft: '2%',
        color: theme.palette.primary.main
    }));

    const StyledLink = styled(Link)({
        fontSize: isLargerThanMedium ? 16 : 13,
        fontWeight: 'bold'
    });

    const StyledTableDataCell = styled(TableDataCell)({
        width: isLargerThanMedium ? '15%' : '17%',
        textAlign: 'center'
    });

    const assignedDate = {
        dateObj: new Date(item.assignedDate + 'Z'),
        get date() {
            return this.dateObj.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
        },
        get time() {
            return this.dateObj.toLocaleTimeString('en-US', {
                timeZone: 'America/New_York',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        }
    };

    React.useEffect(() => {
        setInProgressState(item.inProgress);
    }, [item]);

    return (
        <>
            <TableRow>
                {!removeColumns?.includes('dueDate') && (
                    <TableDataCell>
                        {item.displayDueDate != null ? (
                            <>
                                {item.isUrgent ? (
                                    <RedBox>
                                        {item.displayDueDate}
                                        <BoldTypography>
                                            {item.displayDueTime + ' EST'}
                                        </BoldTypography>
                                    </RedBox>
                                ) : (
                                    <NormalBox>
                                        {item.displayDueDate}
                                        <BoldTypography>
                                            {item.displayDueTime + ' EST'}
                                        </BoldTypography>
                                    </NormalBox>
                                )}
                            </>
                        ) : (
                            ''
                        )}
                    </TableDataCell>
                )}
                {!removeColumns?.includes('assignedDate') && isLargerThanMedium && (
                    <TableDataCell>
                        {item.assignedDate != null ? (
                            <NormalBox>
                                {assignedDate.date}
                                <BoldTypography>{assignedDate.time + ' EST'}</BoldTypography>
                            </NormalBox>
                        ) : (
                            ''
                        )}
                    </TableDataCell>
                )}
                {!removeColumns?.includes('caseNumber') && (
                    <TableDataCell>
                        <Box>
                            <StyledLink to={`/caseDetails/${item.id}/${item.crmClientId}`}>
                                {item.caseNumber}
                            </StyledLink>
                        </Box>
                        {!isLargerThanMedium && (
                            <SemiBoldTypography>{item.caseStage}</SemiBoldTypography>
                        )}
                    </TableDataCell>
                )}
                {!removeColumns?.includes('hasNewMessage') && isLargerThanMedium && (
                    <SemiBoldTableDataCell>
                        {item.hasNewMessage && <StyledEmailIcon />}
                    </SemiBoldTableDataCell>
                )}
                {!removeColumns?.includes('caseStage') && isLargerThanMedium && (
                    <SemiBoldTableDataCell>{item.caseStage}</SemiBoldTableDataCell>
                )}
                <StyledTableDataCell>
                    {!isLargerThanMedium && item.hasNewMessage && <StyledEmailIcon />}
                    {!removeColumns?.includes('inProgress') && (
                        <Checkbox disabled checked={inProgressState} />
                    )}
                </StyledTableDataCell>
            </TableRow>
            {!isLargerThanMedium && !removeColumns?.includes('assignedDate') && (
                <TableRow>
                    <BorderTableDataCell colSpan={4}>
                        {item.assignedDate != null ? (
                            <NormalBox sx={{ display: 'flex', gap: 0.7, color: 'secondary.main' }}>
                                <NoBoldTypography>Received:</NoBoldTypography>
                                <NoBoldTypography>{assignedDate.date}</NoBoldTypography>
                                <NoBoldTypography>-</NoBoldTypography>
                                <NoBoldTypography>{assignedDate.time + ' EST'}</NoBoldTypography>
                            </NormalBox>
                        ) : (
                            ''
                        )}
                    </BorderTableDataCell>
                </TableRow>
            )}
        </>
    );
};

export default ClinicalReviewItem;
